import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PedidoService } from '../../core/services/pedido.service';
import { PagoService } from '../../core/services/pago.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { PedidoDetalleApi, PedidoListApi } from '../../core/models/api/pedido-api';
import { WhatsappUtility } from '../../core/utility/whatsapp-utility';

@Component({
    selector: 'app-admin-verificar',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Por verificar</h2>
        <p class="text-sm text-gray-400 mt-1">Revisa el voucher y aprueba o rechaza el pago de cada pedido.</p>
      </div>
      <label class="block min-w-60">
        <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Buscar</span>
        <input [ngModel]="key()" (ngModelChange)="buscar($event)" placeholder="Nro, correo, teléfono, doc…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white" />
      </label>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="flex flex-col gap-3">
      <p *ngIf="pedidos().length === 0" class="bg-white rounded-[2rem] border border-rose-100 px-5 py-8 text-center text-sm text-gray-400">No hay pagos por verificar.</p>
      <div *ngFor="let p of pedidos()" class="bg-white rounded-[2rem] border border-rose-100 shadow-sm p-5 flex flex-col gap-3">
        <button type="button" (click)="toggle(p.uuid)" class="flex items-center justify-between gap-2 text-left cursor-pointer">
          <span class="font-black text-gray-800">#{{ p.numeroPedido }} <span class="font-semibold text-gray-400 text-xs">{{ p.correoContacto }}</span></span>
          <span class="font-black text-rose-500">S/ {{ p.total.toFixed(2) }}</span>
        </button>
        <div *ngIf="expandido() === p.uuid" class="flex flex-col gap-3 border-t border-gray-100 pt-3">
          <app-ui-loading *ngIf="cargandoDetalle()"></app-ui-loading>
          <div *ngIf="detalle() as d" class="flex flex-col gap-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-600">
              <p><span class="font-bold">Cliente:</span> {{ d.clienteNombre || '—' }}</p>
              <p><span class="font-bold">Documento:</span> {{ d.tipoDocumento ? d.tipoDocumento + ' ' : '' }}{{ d.numeroDocumento || '—' }}</p>
              <p><span class="font-bold">Recibe:</span> {{ d.destinatarioNombre || '—' }}<span *ngIf="d.destinatarioCelular"> · {{ d.destinatarioCelular }}</span></p>
              <p><span class="font-bold">Entrega:</span> {{ d.tipoEntrega }} · {{ d.fechaEntrega }} · {{ d.rangoHorario }}</p>
            </div>
        <div *ngIf="d.establecimientoNombre || d.direccion" class="p-4 bg-gray-50 border border-gray-200 rounded-2xl flex flex-col gap-1 text-sm text-gray-700">
              <p *ngIf="d.establecimientoNombre"><span class="font-bold">Tienda:</span> {{ d.establecimientoNombre }}</p>
              <p *ngIf="d.direccion"><span class="font-bold">Dirección:</span> {{ d.direccion }}<span *ngIf="d.distrito">, {{ d.distrito }}</span></p>
              <p *ngIf="d.referencia"><span class="font-bold">Referencia:</span> {{ d.referencia }}</p>
              <a *ngIf="linkMapa(d) as url" [href]="url" target="_blank" rel="noopener" class="mt-1 inline-flex items-center gap-1 text-xs font-bold text-rose-500 hover:underline">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><circle cx="12" cy="11" r="3" /></svg>
                Ver en Google Maps
              </a>
            </div>

            <ul class="text-sm text-gray-700">
              <li *ngFor="let it of (d.items ?? [])">{{ it.nombre }} × {{ it.cantidad }} — S/ {{ it.precioTotal.toFixed(2) }}</li>
            </ul>
            <div *ngIf="(d.comprobantes ?? []).length" class="flex flex-wrap gap-3">
              <a *ngFor="let url of d.comprobantes" [href]="url" target="_blank" rel="noopener">
                <img [src]="url" alt="Voucher" class="w-28 h-28 object-cover rounded-2xl border border-gray-200" />
              </a>
            </div>
            <p *ngIf="!(d.comprobantes ?? []).length" class="text-xs text-amber-600 font-semibold">Sin voucher adjunto.</p>
            <div *ngIf="d.ocrEstado" class="rounded-2xl border p-4 flex flex-col gap-2"
              [class.border-emerald-200]="d.ocrEstado === 'OK' && !montoNoCoincide(d)"
              [class.bg-emerald-50]="d.ocrEstado === 'OK' && !montoNoCoincide(d)"
              [class.border-amber-200]="d.ocrEstado === 'OK' && montoNoCoincide(d)"
              [class.bg-amber-50]="d.ocrEstado === 'OK' && montoNoCoincide(d)"
              [class.border-gray-200]="d.ocrEstado !== 'OK'"
              [class.bg-gray-50]="d.ocrEstado !== 'OK'">
              <p class="text-xs font-bold uppercase tracking-wide"
                [class.text-emerald-700]="d.ocrEstado === 'OK' && !montoNoCoincide(d)"
                [class.text-amber-700]="d.ocrEstado === 'OK' && montoNoCoincide(d)"
                [class.text-gray-500]="d.ocrEstado !== 'OK'">
                Datos del voucher (OCR)
              </p>
              <div *ngIf="d.ocrEstado === 'OK'" class="grid grid-cols-2 gap-1 text-sm text-gray-700">
                <p><span class="font-bold">N° operación:</span> {{ d.ocrNumeroOperacion || '—' }}</p>
                <p><span class="font-bold">Monto:</span> <span [class.text-red-600]="montoNoCoincide(d)">{{ d.ocrMonto != null ? 'S/ ' + d.ocrMonto.toFixed(2) : '—' }}</span></p>
                <p><span class="font-bold">Fecha:</span> {{ d.ocrFecha ? (d.ocrFecha | date:'short') : '—' }}</p>
              </div>
              <p *ngIf="d.ocrEstado === 'OK' && montoNoCoincide(d)" class="text-xs font-bold text-amber-700">El monto del voucher no coincide con el total del pedido (S/ {{ d.total.toFixed(2) }}).</p>
              <p *ngIf="d.ocrEstado !== 'OK'" class="text-xs text-gray-500">{{ d.ocrError || 'Aún procesando el voucher…' }}</p>
              <details *ngIf="d.ocrTexto" class="text-[11px] text-gray-500">
                <summary class="cursor-pointer font-bold">Texto OCR</summary>
                <pre class="whitespace-pre-wrap mt-1">{{ d.ocrTexto }}</pre>
              </details>
            </div>
            <div class="rounded-2xl border border-gray-200 p-4 flex flex-col gap-3">
              <p class="text-xs font-bold uppercase tracking-wide text-gray-500">Decisión del pago</p>
              <div *ngIf="aprobando() !== p.uuid" class="flex flex-wrap items-center gap-2">
                <button type="button" (click)="aprobando.set(p.uuid)" class="px-5 py-2.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold cursor-pointer">Aprobar pago</button>
                <button type="button" (click)="rechazando.set(rechazando() === p.uuid ? null : p.uuid)" class="px-5 py-2.5 rounded-2xl border border-red-200 text-red-500 hover:bg-red-50 text-sm font-bold cursor-pointer">Rechazar pago</button>
                <a [href]="waCliente(d)" target="_blank" rel="noopener" class="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-50 text-emerald-600 text-sm font-bold rounded-2xl hover:bg-emerald-100 cursor-pointer">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h8m-8 4h8m-8-8h8M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  WhatsApp
                </a>
              </div>
              <div *ngIf="aprobando() === p.uuid" class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-emerald-700">¿Confirmas que el voucher es válido?</span>
                <button type="button" (click)="aprobar(p)" class="px-5 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold cursor-pointer">Sí, aprobar</button>
                <button type="button" (click)="aprobando.set(null)" class="px-5 py-2.5 rounded-2xl border border-gray-200 text-gray-600 text-sm font-bold cursor-pointer">Cancelar</button>
              </div>
              <div *ngIf="rechazando() === p.uuid" class="flex flex-wrap items-center gap-2">
                <input [(ngModel)]="motivo" placeholder="Motivo del rechazo" class="flex-1 min-w-40 rounded-2xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
                <button type="button" (click)="rechazar(p)" [disabled]="!motivo().trim()" class="px-5 py-2.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold disabled:opacity-40 cursor-pointer">Confirmar rechazo</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    `
})
export class AdminVerificarComponent implements OnInit {
    private pedidosService = inject(PedidoService);
    private pagos = inject(PagoService);
    private alerts = inject(AlertService);

    loading = signal(true);
    key = signal('');
    pedidos = signal<PedidoListApi[]>([]);
    rechazando = signal<string | null>(null);
    aprobando = signal<string | null>(null);
    private searchTimer: ReturnType<typeof setTimeout> | null = null;
    expandido = signal<string | null>(null);
    detalle = signal<PedidoDetalleApi | null>(null);
    cargandoDetalle = signal(false);
    motivo = signal('');

    ngOnInit(): void {
        this.cargar();
    }

    toggle(uuid: string) {
        if (this.expandido() === uuid) {
            this.expandido.set(null);
            this.detalle.set(null);
            return;
        }
        this.expandido.set(uuid);
        this.detalle.set(null);
        this.motivo.set('');
        this.rechazando.set(null);
        this.cargandoDetalle.set(true);
        this.pedidosService.getPedido(uuid).subscribe({
            next: (d) => {
                this.detalle.set(d);
                this.cargandoDetalle.set(false);
            },
            error: (err) => {
                this.cargandoDetalle.set(false);
                this.alerts.error(err?.message || 'No se pudo cargar el pedido.');
            }
        });
    }



    linkMapa(d: PedidoDetalleApi): string {
        if (d.urlMaps) {
            return d.urlMaps;
        }
        if (d.latitud != null && d.longitud != null) {
            return `https://www.google.com/maps/dir/?api=1&destination=${d.latitud},${d.longitud}`;
        }
        if (d.establecimientoNombre) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.establecimientoNombre)}`;
        }
        return '';
    }

    montoNoCoincide(d: PedidoDetalleApi): boolean {
        return d.ocrMonto != null && Math.abs(d.ocrMonto - d.total) > 0.01;
    }

    aprobar(p: PedidoListApi) {
        this.aprobando.set(null);
        this.pagos.aprobarPago(p.uuid).subscribe({
            next: () => {
                this.alerts.ok(`Pedido #${p.numeroPedido} aprobado`);
                this.expandido.set(null);
                this.detalle.set(null);
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo aprobar.')
        });
    }

    rechazar(p: PedidoListApi) {
        if (!this.motivo().trim()) {
            return;
        }
        this.pagos.rechazarPago(p.uuid, this.motivo().trim()).subscribe({
            next: () => {
                this.alerts.ok(`Pedido #${p.numeroPedido} rechazado`);
                this.expandido.set(null);
                this.detalle.set(null);
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo rechazar.')
        });
    }

    buscar(term: string) {
        this.key.set(term);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => this.cargar(), 400);
    }

    waCliente(d: PedidoDetalleApi): string {
        const text = `Hola, te escribimos por tu pedido #${d.numeroPedido} de S/ ${d.total.toFixed(2)}. Estamos verificando tu pago.`;
        const direct = WhatsappUtility.waLink(d.telefonoContacto, text);
        return direct || `https://wa.me/?text=${encodeURIComponent(text)}`;
    }

    private cargar() {
        this.loading.set(true);
        this.pedidosService.listPedidosStaff('PAGO_POR_VERIFICAR', this.key() || undefined, 1, 50).subscribe({
            next: (res) => {
                this.pedidos.set(res.data);
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar los pedidos.');
            }
        });
    }
}
